<template>
  <v-dialog
    v-model="display"
    width="1000"
    dark
    @click:outside="handleClose"
  >
    <v-card class="modal">
      <v-card-title class="pa-4 pl-11">
        <div>{{ $t('profile.label') }}</div>
        <v-icon @click="handleClose">mdi-close</v-icon>
      </v-card-title>
      <v-divider />
      <v-card-text class="px-12 py-7">
        <v-col>
          <v-row class="mb-1">
            <div class="text">{{ $t('profile.username') }}</div>
          </v-row>
          <v-row>
            <v-text-field
              outlined
              dark
              dense
              :value="getCurrentUserName"
              :color="props.themeColors.secondary"
              disabled
            />
          </v-row>
          <v-row class="mb-1">
            <div class="text">{{ $t('profile.email') }}</div>
          </v-row>
          <v-row>
            <v-text-field
              outlined
              dark
              dense
              :value="getCurrentUserEmail"
              :color="props.themeColors.secondary"
              disabled
            />
          </v-row>
          <v-row>
            <v-col md="3" class="py-0">
              <v-row class="mb-1">
                <div class="text">{{ $t('profile.language') }}</div>
              </v-row>
              <v-row>
                <v-select
                  v-model="locale"
                  dark
                  outlined
                  dense
                  hide-details
                  :items="props.localesAvailable"
                  return-object
                  solo
                >
                  <template v-slot:selection="{ item }">
                    <v-list-item dense dark>
                      <v-list-item-icon class="mr-0">
                        <img
                          :src="
                            require(`@/assets/icons/flags/${item.value}.svg`)
                          "
                          :alt="`icon-${item.value}`"
                        >
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ item.caption }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>

                  <template v-slot:item="{ item }">
                    <v-list-item-group dense>
                      <v-list-item disabled>
                        <v-list-item-icon class="mr-0">
                          <img
                            :src="require(`@/assets/icons/flags/${item.value}.svg`)"
                            :alt="`icon-${item.value}`"
                          >
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ item.caption }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list-item-group>
                  </template>
                </v-select>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-card-text>
      <v-divider />
      <div class="actions pa-4 pl-11">
        <v-btn
          dark
          class="ctp-btn my-0"
          :color="props.themeColors.secondary"
          @click="handleSave"
        >
          {{ $t('profile.buttons.save') }}
        </v-btn>
        <v-spacer />
        <v-btn
          dark
          class="ctp-btn my-0 cancel"
          :color="props.themeColors.secondary"
          @click="handleClose"
        >
          {{ $t('profile.buttons.cancel') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang='ts'>
import { colors } from '@/constants/theme/colors';
import { LOCALES } from '@/constants/i18n/locales';
import { computed, PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n-composable';
import useStore from '@/utils/store/common';

interface ILocale {
  value: string;
  caption: string;
}

const props = defineProps({
  localesAvailable: {
    type: Array as PropType<ILocale[]>,
    default: () => LOCALES
  },
  enabled: {
    type: Boolean
  },
  themeColors: {
    type: Object,
    default: () => colors
  }
});

const emit = defineEmits(['close']);

const i18n = useI18n();

const store = useStore();
const getCurrentUserName = computed(() => store.getters['user/getCurrentUserName']);
const getCurrentUserEmail = computed(() => store.getters['user/getCurrentUserEmail']);

const display = computed(() => props.enabled);

const currentLocale = computed(() => {
  let locale: ILocale = LOCALES[0];
  if (props.localesAvailable) {
    locale = props.localesAvailable.find(lc => lc.value === i18n.locale.value) || LOCALES[0];
  }

  return locale;
});

const locale = ref(currentLocale.value);

watch(
  () => i18n.locale.value,
  () => {
    locale.value = currentLocale.value;
  },
  {
    immediate: true
  }
);

function handleClose() {
  emit('close');
}

async function handleSave() {
  i18n.locale.value = locale.value.value;
  await store.dispatch('user/updateUserLocale', { locale: i18n.locale.value });
  handleClose();
}
</script>

<style lang="scss" scoped>
@import './styles/user-profile.scss';
</style>
